<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:file-image',
	name: '$t:blocks.input_file_m2o.name',
	description: '$t:blocks.input_file_m2o.description',
	category: 'input',
	tags: ['form-control'],
	hidden: ({ isRoot }) => isRoot,
}
</script>
<script lang="ts" setup>
import type { UploadId } from '#layers/assets/public';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { CompositePrimaryKey, GlobalSingleRelationFieldId, MonospaceFile } from '#layers/data-model/public';
import { BlockRenderInputSelectM2O } from 'virtual:monospace-builtin-blocks';
import { computed, ref } from 'vue';
import { FileUploadProgress, useFileUpload, useUploadStore } from '#layers/assets/public';
import { defineBlock, isBlockSerialized, useBlockLibraryStore } from '#layers/block-engine/public';
import { useCanUploadFiles } from '#layers/blocks/composables/relation/use-can-upload-files';
import { useM2OSelection } from '#layers/blocks/composables/relation/use-m2o-selection';
import { useM2OState } from '#layers/blocks/composables/relation/use-m2o-state';
import { getRelationReferenceKeyFields, pickByPrimitiveFields } from '#layers/data-model/public';
import { injectFormBlockContext, useM2OPermissions, useRelationFormField } from '#layers/forms/public';

interface InputFileM2OBlockProps extends InputBlockProps {
	fieldId?: GlobalSingleRelationFieldId;
	referencedItemKey?: CompositePrimaryKey;
	showRemove?: boolean;
}

const {
	// Optional boolean props are cast to `false` when absent (native-attr semantics);
	// default to `true` so the remove button shows unless a field opts out explicitly.
	showRemove: showRemoveProp = true,
	readonly,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	fieldId,
} = defineProps<InputFileM2OBlockProps>();

;

const { field, referencedItemKey, emitConnect, emitDisconnect, fieldValue, disabled, disabledNotice } = useRelationFormField<'to-one'>(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp, fieldId }));

const referenceKeyFields = computed(() => field.value ? getRelationReferenceKeyFields(field.value) : []);
const { item, effectiveKey, isCreate } = useM2OState(field, referencedItemKey, fieldValue, {
	additionalSelection: () => ({
		fields: referenceKeyFields.value.map((referenceField) => referenceField.apiName),
	}),
});

const formBlockContext = injectFormBlockContext(null);
const permissions = useM2OPermissions({
	field,
	writeContext: () => formBlockContext?.writeContext.value,
});
// An upload creates the file through the assets endpoint and then connects it.
const canUploadFiles = useCanUploadFiles();
const canUpload = computed(() => permissions.canConnect.value && canUploadFiles.value);

const { useFilteredBlocks } = useBlockLibraryStore();

const { filteredBlocks: previewBlocks } = useFilteredBlocks({
	filter: (block) => {
		if (isBlockSerialized(block)) return false;
		if (block.category !== 'file-preview') return false;

		return !!block.tags?.some((tag) => item.value.mediaType?.startsWith(tag));
	},
});
const previewBlock = computed(() => previewBlocks.value[0]);

const showRemove = computed(() => {
	if (showRemoveProp === false) return false;
	if (readonly) return false;
	if (disabled.value) return false;

	return permissions.canDisconnect.value;
});

const { handleSelect, handleDeselect } = useM2OSelection({
	field,
	referenceKeyFields,
	effectiveKey,
	isCreate,
	referencedItemKey,
	emitConnect,
	emitDisconnect,
});

const uploadStore = useUploadStore();
const currentUploadId = ref<UploadId | null>(null);
const currentUpload = computed(() => currentUploadId.value ? uploadStore.getFile(currentUploadId.value) : null);

const { uploadFiles } = useFileUpload({
	onStart: (uploadId) => {
		currentUploadId.value = uploadId;
	},
});

function resetFileUpload() {
	currentUploadId.value = null;
}

function abortUpload() {
	if (!currentUploadId.value) return;

	uploadStore.abortUpload(currentUploadId.value);
	resetFileUpload();
}

async function onFileUpload(file: File) {
	resetFileUpload();

	const [uploadedFile] = await uploadFiles([file]);

	if (uploadedFile) {
		emitConnect(pickByPrimitiveFields(uploadedFile, referenceKeyFields.value));
	}
}
</script>

<template>
	<BlockRender
		v-if="item && previewBlock"
		:block="previewBlock.id"
		:props="{
			file: item as MonospaceFile,
			showRemove,
			onRemove: handleDeselect,
		}"
	/>
	<BlockRenderInputSelectM2O
		v-else-if="item"
		:field-id="fieldId!"
		:referenced-item-key="referencedItemKey!"
		:disabled
		:disabled-notice
		:readonly
		:show-remove
	/>
	<FileUploadError v-else-if="currentUpload && currentUpload.status === 'error'" :upload="currentUpload" @remove="resetFileUpload" />
	<FileUploadProgress v-else-if="currentUpload?.status === 'uploading'" :upload="currentUpload" @abort="abortUpload" />
	<FileUpload
		v-else
		:multiple="false"
		browse-existing
		:browse-disabled="!permissions.canConnect.value"
		:upload-disabled="!canUpload"
		:browse-disabled-reason="$t('permissions.no_connect_access')"
		:upload-disabled-reason="$t('permissions.no_create_access')"
		@browse="handleSelect"
		@update:model-value="onFileUpload"
	/>
</template>
